import { computed, ref, type Ref, watch, onScopeDispose, h } from 'vue';
import { toast } from './toast/use-toast';
import { watchTriggerable } from '@vueuse/core';

export function watchAndFetch<Input, Result>(
  watching: Ref<Input>,
  defaultValue: Result,
  func: (input: Input) => Promise<Result>,
  canFetch?: Ref<boolean>,
  options?: {
    keepPreviousOnRefresh?: boolean;
    /** Compare request scope when retaining data; defaults to reference/primitive identity. */
    inputsEqual?: (previous: Input, next: Input) => boolean;
  },
): { loading: Ref<boolean>; response: Ref<Result>; error: Ref<Error | null>; trigger: () => unknown } {
  const response = ref(defaultValue) as Ref<Result>;
  const error = ref<Error | null>(null);
  const currentInputCounter = ref<number>(0);
  const currentInputLoaded = ref<number>(-1);
  let fetchSkipped = false;
  let previousInput: Input;
  let hasPreviousInput = false;
  let disposed = false;

  const { trigger } = watchTriggerable(
    watching,
    async (input) => {
      if (disposed) return;
      if (canFetch && !canFetch.value) {
        fetchSkipped = true;
        currentInputLoaded.value = ++currentInputCounter.value;
        return;
      }
      fetchSkipped = false;

      // Manual refreshes can retain the current view while new data is fetched.
      // A changed input must still clear data from the previous facility/year.
      const sameInput =
        hasPreviousInput &&
        (options?.inputsEqual ? options.inputsEqual(previousInput, input) : input === previousInput);
      if (!options?.keepPreviousOnRefresh || !sameInput) response.value = defaultValue;
      previousInput = input;
      hasPreviousInput = true;
      currentInputCounter.value += 1;
      const inputID = currentInputCounter.value;
      error.value = null;
      try {
        const valueFetched = await func(input);
        if (currentInputCounter.value === inputID) response.value = valueFetched;
      } catch (cause) {
        if (currentInputCounter.value === inputID) {
          error.value = cause instanceof Error ? cause : new Error('Unable to load data.');
          toast({
            title: 'Unable to load data',
            description: h('button', { class: 'underline', onClick: () => trigger() }, 'Try again'),
            variant: 'destructive',
          });
        }
      } finally {
        if (currentInputCounter.value === inputID) currentInputLoaded.value = inputID;
      }
    },
    { immediate: true },
  );
  onScopeDispose(() => {
    disposed = true;
    currentInputLoaded.value = ++currentInputCounter.value;
  });

  if (canFetch) {
    watch(canFetch, (newVal) => {
      if (!newVal) {
        fetchSkipped = true;
        currentInputLoaded.value = ++currentInputCounter.value;
        response.value = defaultValue;
        error.value = null;
      }
      if (newVal && fetchSkipped) {
        fetchSkipped = false;
        trigger();
      }
    });
  }

  return {
    loading: computed(() => currentInputLoaded.value !== currentInputCounter.value),
    response,
    error,
    trigger,
  };
}
